import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Button
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import CurriculumTimeline from './CurriculumTimeline';

const MotionBox = motion(Box);

/**
 * Course Curriculum Section
 * Displays the course structure as an interactive timeline
 */
const CourseCurriculum = ({ data, onEnrollClick }) => {
  if (!data || !data.timeline?.length) return null;

  const { title, subtitle, description, timeline } = data;

  // Count total lessons
  const totalLessons = timeline.reduce((sum, subject) => sum + subject.lessons.length, 0);

  return (
    <Box py={{ base: 16, md: 20 }} bg="white" borderTop="1px solid" borderColor="gray.100">
      <Container maxW="container.lg">
        <MotionBox
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
        >
          <VStack align="start" spacing={{ base: 6, md: 8 }} w="full">
            {/* Section Header */}
            <VStack align="start" spacing={2} w="full">
              {title && (
                <Heading
                  as="h2"
                  size="2xl"
                  fontWeight="900"
                  color="gray.900"
                >
                  {title}
                </Heading>
              )}
              {subtitle && (
                <Text
                  fontSize={{ base: 'md', md: 'lg' }}
                  color="gray.600"
                  fontWeight="500"
                >
                  {subtitle}
                </Text>
              )}
              {description && (
                <Text
                  fontSize="md"
                  color="gray.600"
                  maxW="2xl"
                >
                  {description}
                </Text>
              )}

              {/* Stats */}
              <Box
                display="flex"
                gap={8}
                mt={2}
                flexWrap="wrap"
              >
                <Box>
                  <Text fontSize="2xl" fontWeight="900" color="brand.600">
                    {timeline.length}
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    {timeline.length === 1 ? 'Subject' : 'Subjects'}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="2xl" fontWeight="900" color="brand.600">
                    {totalLessons}
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    {totalLessons === 1 ? 'Lesson' : 'Lessons'}
                  </Text>
                </Box>
              </Box>
            </VStack>

            {/* Timeline */}
            <Box w="full" mt={8}>
              <CurriculumTimeline timeline={timeline} />
            </Box>

            {/* Call to Action */}
            <Box w="full" pt={4} textAlign="center">
              <Text fontSize="sm" color="gray.600" mb={4}>
                Ready to get started?
              </Text>
              <Button
                onClick={onEnrollClick}
                colorScheme="brand"
                size="lg"
                fontWeight="700"
              >
                Enroll Now
              </Button>
            </Box>
          </VStack>
        </MotionBox>
      </Container>
    </Box>
  );
};

export default CourseCurriculum;
